import { useEffect, useState } from "react";

// Best-effort protection against screenshots / printing / saving.
// Cannot truly block OS-level screenshots, but does everything a browser can.
export default function ProtectionLayer() {
  const [blurred, setBlurred] = useState(false);

  useEffect(() => {
    const prevent = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const onKey = (e) => {
      const k = e.key?.toLowerCase();
      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      // Block Ctrl+P (print), Ctrl+S (save), Ctrl+Shift+S/P/I (screenshot/devtools), F12, PrintScreen
      if (
        (ctrlOrMeta && (k === "p" || k === "s")) ||
        (ctrlOrMeta && e.shiftKey && (k === "s" || k === "p" || k === "i")) ||
        k === "f12" ||
        k === "printscreen"
      ) {
        prevent(e);
      }
    };

    // Block Ctrl+C / Ctrl+X (copy / cut) originating from resume input fields
    // AND from the live preview surface, so the entered/rendered résumé
    // content can't be extracted via the clipboard.
    // Paste (Ctrl+V) remains allowed so users can still paste content in.
    const onCopyCut = (e) => {
      const t = e.target;
      if (!t || typeof t.closest !== "function") return;
      if (
        (typeof t.matches === "function" && t.matches("input, textarea")) ||
        t.closest(".paper") ||
        t.closest("[data-testid='resume-preview']")
      ) {
        prevent(e);
      }
    };

    const onBlur = () => {
      // While a payment modal is open, Razorpay steals window focus. Suppress
      // the blur overlay so the app doesn't get stuck in "Content hidden".
      if (window.__paymentInFlight) return;
      setBlurred(true);
    };
    const onFocus = () => setBlurred(false);
    const onVisibility = () => {
      if (document.hidden && window.__paymentInFlight) return;
      setBlurred(document.hidden);
    };
    // Custom event fired by payment.js when the Razorpay modal closes (success,
    // failure OR user-cancel). Force-clears the blur overlay so the user isn't
    // stuck looking at "return to tab" after cancelling a payment.
    const onPaymentEnd = () => setBlurred(false);

    document.addEventListener("contextmenu", prevent);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("keyup", onKey, true);
    document.addEventListener("copy", onCopyCut, true);
    document.addEventListener("cut", onCopyCut, true);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("myresumedesign:payment-end", onPaymentEnd);

    const onBeforePrint = (e) => {
      // Allow the Download PDF button's programmatic window.print() through.
      if (window.__allowPrint) return;
      prevent(e);
      window.alert("Printing is disabled on this page.");
    };
    window.addEventListener("beforeprint", onBeforePrint);

    return () => {
      document.removeEventListener("contextmenu", prevent);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("keyup", onKey, true);
      document.removeEventListener("copy", onCopyCut, true);
      document.removeEventListener("cut", onCopyCut, true);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("myresumedesign:payment-end", onPaymentEnd);
      window.removeEventListener("beforeprint", onBeforePrint);
    };
  }, []);

  if (!blurred) return null;
  return (
    <div
      className="fixed inset-0 z-[9998] backdrop-blur-3xl bg-white/70 flex items-center justify-center pointer-events-none"
      data-testid="privacy-blur"
    >
      <div className="font-heading font-bold text-2xl tracking-tight text-neutral-800">
        Content hidden — return to tab to view
      </div>
    </div>
  );
}
